"use client";

import * as React from "react";
import { Command as CommandPrimitive } from "cmdk";
import {
  SearchIcon,
  FolderIcon,
  LayoutGridIcon,
  PlusIcon,
  Settings2,
  KeyboardIcon,
  Grid3x3,
  Layers,
  Waypoints,
  Cpu,
  ScanEye,
  MessageSquarePlus,
  Box,
  Square,
  History,
  Bug,
  Ghost,
} from "lucide-react";
import type { AppContext } from "./command-palette-provider";
import { Dialog, DialogContent, DialogTitle } from "@/ui/dialog";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/ui/card";
import { formatHotkeyCompact } from "@/lib/utils/format-hotkey";

type CommandPaletteProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context: AppContext;
  pathname: string;
  onNavigate: (url: string) => void;
  onAction: (action: () => void) => void;
  onOpenSettings: () => void;
  onOpenHotkeyHelp: () => void;
  onOpenFeedback: () => void;
  feedbackEnabled: boolean;
  onResetView: () => void;
  onToggleOrthographic: () => void;
  orthographic: boolean;
  onToggleSpookyMode: () => void;
  spookyMode: boolean;
  onToggleHistory: () => void;
  historyEnabled: boolean | null;
  onToggleDebug: () => void;
  debugOpen: boolean;
  debugAvailable: boolean;
};

const OPERATION_SUB_PAGES = [
  { id: "mesh", label: "Mesh", icon: Grid3x3 },
  { id: "slice", label: "Slice", icon: Layers },
  { id: "path-planning", label: "Path Planning", icon: Waypoints },
  { id: "cell-settings", label: "Cell Sim", icon: Settings2 },
  { id: "kinematics", label: "Kinematics", icon: Cpu },
] as const;

function Shortcut({ keys }: { keys: string }) {
  return (
    <kbd className="ml-auto shrink-0 rounded border bg-muted px-1.5 font-mono text-[10px] text-muted-foreground">
      {formatHotkeyCompact(keys)}
    </kbd>
  );
}

export function CommandPalette(props: CommandPaletteProps) {
  const {
    open,
    onOpenChange,
    context,
    pathname,
    onNavigate,
    onOpenSettings,
    onOpenHotkeyHelp,
    onOpenFeedback,
    feedbackEnabled,
    onResetView,
    onToggleOrthographic,
    orthographic,
    onToggleSpookyMode,
    spookyMode,
    onToggleHistory,
    historyEnabled,
    onToggleDebug,
    debugOpen,
    debugAvailable,
  } = props;

  const [search, setSearch] = React.useState("");

  // Show the projection the command switches TO (the opposite of the current mode).
  const ProjectionIcon = orthographic ? Box : Square;

  const handleOpenChange = (next: boolean) => {
    if (!next) setSearch("");
    onOpenChange(next);
  };

  const operationBase =
    context.level === "operation"
      ? `/projects/${context.projectId}/parts/${context.partId}/procedures/${context.procedureId}/operations/${context.operationId}`
      : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="p-0 border-0 shadow-none bg-transparent w-auto max-w-none"
        showCloseButton={false}
      >
        <Card className="w-[520px] max-w-[calc(100vw-2rem)] overflow-hidden bg-background">
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <DialogTitle asChild>
              <CardTitle>Command Palette</CardTitle>
            </DialogTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-hidden">
            <CommandPrimitive shouldFilter={true}>
              <div className="flex items-center gap-2 border-b px-3">
                <SearchIcon className="size-4 shrink-0 opacity-50" />
                <CommandPrimitive.Input
                  value={search}
                  onValueChange={setSearch}
                  placeholder="Type a command or search..."
                  className="placeholder:text-muted-foreground flex h-11 w-full bg-transparent py-3 text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>

              <CommandPrimitive.List className="max-h-100 overflow-y-auto overflow-x-hidden [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group]+[cmdk-group]]:border-t [&_[cmdk-group]+[cmdk-group]]:border-border/50">
                <CommandPrimitive.Empty className="py-6 text-center text-sm text-muted-foreground">
                  No commands found.
                </CommandPrimitive.Empty>

                {/* Go To group — context-aware navigation within the app */}
                {context.level !== "global" && (
                  <CommandPrimitive.Group heading="Go To">
                    <CommandPrimitive.Item
                      value="go to projects list"
                      onSelect={() => onNavigate("/projects")}
                      className="flex cursor-pointer items-center gap-3 rounded-none px-4 py-2 text-sm aria-selected:bg-muted"
                    >
                      <LayoutGridIcon className="size-4 shrink-0 text-muted-foreground" />
                      <span>Projects</span>
                    </CommandPrimitive.Item>

                    {(context.level === "part" ||
                      context.level === "procedure" ||
                      context.level === "operation") && (
                      <CommandPrimitive.Item
                        value="go to part overview"
                        onSelect={() =>
                          onNavigate(`/projects/${context.projectId}/parts/${context.partId}`)
                        }
                        className="flex cursor-pointer items-center gap-3 rounded-none px-4 py-2 text-sm aria-selected:bg-muted"
                      >
                        <FolderIcon className="size-4 shrink-0 text-muted-foreground" />
                        <span>Part Overview</span>
                      </CommandPrimitive.Item>
                    )}

                    {operationBase &&
                      OPERATION_SUB_PAGES.map(({ id, label, icon: Icon }) => {
                        const isActive = pathname.endsWith(`/${id}`);
                        return (
                          <CommandPrimitive.Item
                            key={id}
                            value={`go to ${label.toLowerCase()}`}
                            onSelect={() => onNavigate(`${operationBase}/${id}`)}
                            className="flex cursor-pointer items-center gap-3 rounded-none px-4 py-2 text-sm aria-selected:bg-muted"
                          >
                            <Icon
                              className={`size-4 shrink-0 ${isActive ? "text-primary-text" : "text-muted-foreground"}`}
                            />
                            <span className={isActive ? "font-medium" : ""}>{label}</span>
                            {isActive && (
                              <span className="ml-auto text-xs text-muted-foreground">current</span>
                            )}
                          </CommandPrimitive.Item>
                        );
                      })}
                  </CommandPrimitive.Group>
                )}

                {/* Actions group — commands that do something */}
                <CommandPrimitive.Group heading="Actions">
                  {(context.level === "part" ||
                    context.level === "procedure" ||
                    context.level === "operation") && (
                    <CommandPrimitive.Item
                      value="create procedure"
                      onSelect={() =>
                        onNavigate(
                          `/projects/${context.projectId}/parts/${context.partId}/create-procedure`
                        )
                      }
                      className="flex cursor-pointer items-center gap-3 rounded-none px-4 py-2 text-sm aria-selected:bg-muted"
                    >
                      <PlusIcon className="size-4 shrink-0 text-muted-foreground" />
                      <span>Create Procedure</span>
                      {context.level === "part" && <Shortcut keys="c" />}
                    </CommandPrimitive.Item>
                  )}

                  {(context.level === "procedure" || context.level === "operation") && (
                    <CommandPrimitive.Item
                      value="create operation"
                      onSelect={() =>
                        onNavigate(
                          `/projects/${context.projectId}/parts/${context.partId}/procedures/${context.procedureId}/create-operation`
                        )
                      }
                      className="flex cursor-pointer items-center gap-3 rounded-none px-4 py-2 text-sm aria-selected:bg-muted"
                    >
                      <PlusIcon className="size-4 shrink-0 text-muted-foreground" />
                      <span>Create Operation</span>
                      <Shortcut keys="c" />
                    </CommandPrimitive.Item>
                  )}

                  {(context.level === "part" ||
                    context.level === "procedure" ||
                    context.level === "operation") && (
                    <CommandPrimitive.Item
                      value="reset view camera viewport 3d"
                      onSelect={() => onResetView()}
                      className="flex cursor-pointer items-center gap-3 rounded-none px-4 py-2 text-sm aria-selected:bg-muted"
                    >
                      <ScanEye className="size-4 shrink-0 text-muted-foreground" />
                      <span>Reset View</span>
                    </CommandPrimitive.Item>
                  )}

                  {(context.level === "part" ||
                    context.level === "procedure" ||
                    context.level === "operation") && (
                    <CommandPrimitive.Item
                      value="orthographic perspective projection camera view 3d"
                      onSelect={() => onToggleOrthographic()}
                      className="flex cursor-pointer items-center gap-3 rounded-none px-4 py-2 text-sm aria-selected:bg-muted"
                    >
                      <ProjectionIcon className="size-4 shrink-0 text-muted-foreground" />
                      <span>
                        {orthographic ? "Switch to Perspective" : "Switch to Orthographic"}
                      </span>
                    </CommandPrimitive.Item>
                  )}

                  {(context.level === "part" ||
                    context.level === "procedure" ||
                    context.level === "operation") && (
                    <CommandPrimitive.Item
                      value="spooky mode halloween cobwebs spider webs toggle enable disable 3d"
                      onSelect={() => onToggleSpookyMode()}
                      className="flex cursor-pointer items-center gap-3 rounded-none px-4 py-2 text-sm aria-selected:bg-muted"
                    >
                      <Ghost className="size-4 shrink-0 text-muted-foreground" />
                      <span>{spookyMode ? "Disable Spooky Mode" : "Enable Spooky Mode"}</span>
                    </CommandPrimitive.Item>
                  )}

                  {/* A per-viewer opt-in rather than a Datadog change: the flag decides the
                      default, this decides whether *you* see the entry point. Always listed, so
                      someone who has turned it off can find it again. */}
                  <CommandPrimitive.Item
                    value="version history asset lineage versions toggle show hide"
                    onSelect={() => onToggleHistory()}
                    className="flex cursor-pointer items-center gap-3 rounded-none px-4 py-2 text-sm aria-selected:bg-muted"
                  >
                    <History className="size-4 shrink-0 text-muted-foreground" />
                    <span>
                      {historyEnabled === null
                        ? "Toggle Version History"
                        : historyEnabled
                          ? "Hide Version History"
                          : "Show Version History"}
                    </span>
                  </CommandPrimitive.Item>

                  {debugAvailable && (
                    <CommandPrimitive.Item
                      value="debug mode internals scene state backend diagnostics toggle"
                      onSelect={() => onToggleDebug()}
                      className="flex cursor-pointer items-center gap-3 rounded-none px-4 py-2 text-sm aria-selected:bg-muted"
                    >
                      <Bug className="size-4 shrink-0 text-muted-foreground" />
                      <span>{debugOpen ? "Hide Debug Mode" : "Toggle Debug Mode"}</span>
                    </CommandPrimitive.Item>
                  )}

                  <CommandPrimitive.Item
                    value="open settings preferences"
                    onSelect={onOpenSettings}
                    className="flex cursor-pointer items-center gap-3 rounded-none px-4 py-2 text-sm aria-selected:bg-muted"
                  >
                    <Settings2 className="size-4 shrink-0 text-muted-foreground" />
                    <span>Open Settings</span>
                    <Shortcut keys="mod+comma" />
                  </CommandPrimitive.Item>

                  <CommandPrimitive.Item
                    value="keyboard shortcuts help"
                    onSelect={onOpenHotkeyHelp}
                    className="flex cursor-pointer items-center gap-3 rounded-none px-4 py-2 text-sm aria-selected:bg-muted"
                  >
                    <KeyboardIcon className="size-4 shrink-0 text-muted-foreground" />
                    <span>Keyboard Shortcuts</span>
                    <Shortcut keys="shift+slash" />
                  </CommandPrimitive.Item>

                  {feedbackEnabled && (
                    <CommandPrimitive.Item
                      value="give feedback report bug idea"
                      onSelect={onOpenFeedback}
                      className="flex cursor-pointer items-center gap-3 rounded-none px-4 py-2 text-sm aria-selected:bg-muted"
                    >
                      <MessageSquarePlus className="size-4 shrink-0 text-muted-foreground" />
                      <span>Give Feedback</span>
                      <Shortcut keys="mod+shift+f" />
                    </CommandPrimitive.Item>
                  )}
                </CommandPrimitive.Group>
              </CommandPrimitive.List>
            </CommandPrimitive>
          </CardContent>
          <CardFooter className="justify-end">
            <span className="text-muted-foreground flex items-center gap-1 text-xs">
              <kbd className="bg-muted pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border px-1.5 font-mono text-[10px] font-medium">
                Esc
              </kbd>
              to close
            </span>
          </CardFooter>
        </Card>
      </DialogContent>
    </Dialog>
  );
}
